import type { PerformanceDatapoint } from 'owa-analytics';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

export function isMailListLoadGenerationEnabled(): boolean {
    return isGlobalFeatureEnabled('tri-mll-generation-completion');
}

/**
 * Performance datapoint that tracks the time from when we request data to load the mail list
 * to when the corresponding mail list generation is rendered.
 */
export interface MailListLoadDatapointContext {
    datapoint: PerformanceDatapoint;
    tableViewId: string;
    loadId: number;
    isReadyToRender: boolean;
    owningSwitchMailFolderDatapoint?: PerformanceDatapoint;
}

let currentContext: MailListLoadDatapointContext | undefined;
let nextLoadId = 0;
const changeListeners = new Set<() => void>();

export function getMailListLoadDatapointContext(): MailListLoadDatapointContext | undefined {
    return currentContext;
}

export function getReadyMailListLoadId(tableViewId: string): number | undefined {
    return currentContext?.tableViewId === tableViewId && currentContext.isReadyToRender
        ? currentContext.loadId
        : undefined;
}

export function getActiveMailListLoadId(tableViewId: string): number | undefined {
    return currentContext?.tableViewId === tableViewId ? currentContext.loadId : undefined;
}

export function setMailListLoadDatapoint(
    datapoint: PerformanceDatapoint,
    tableViewId: string,
    owningSwitchMailFolderDatapoint?: PerformanceDatapoint
): number {
    const loadId = ++nextLoadId;
    currentContext = {
        datapoint,
        tableViewId,
        loadId,
        isReadyToRender: false,
        owningSwitchMailFolderDatapoint,
    };
    notifyChangeListeners();
    return loadId;
}

export function markMailListLoadReadyToRender(tableViewId: string, loadId: number): void {
    if (
        currentContext?.tableViewId === tableViewId &&
        currentContext.loadId === loadId &&
        !currentContext.isReadyToRender
    ) {
        currentContext.isReadyToRender = true;
        currentContext.datapoint.addCustomData({ readyToRender: true });
        notifyChangeListeners();
    }
}

export function clearMailListLoadDatapoint(tableViewId: string, loadId: number): void {
    if (currentContext?.tableViewId === tableViewId && currentContext.loadId === loadId) {
        currentContext = undefined;
        notifyChangeListeners();
    }
}

export function subscribeToMailListLoadChanges(listener: () => void): () => void {
    changeListeners.add(listener);
    return () => changeListeners.delete(listener);
}

function notifyChangeListeners(): void {
    changeListeners.forEach(listener => listener());
}
